import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlinePencilAlt } from "react-icons/hi";
import { IoSearch, IoEyeOutline, IoTrashOutline } from "react-icons/io5";
import { IoMdArchive } from "react-icons/io";
import { MdOutlineWifiTetheringErrorRounded } from "react-icons/md";
import Search from "../../Jobick/Jobs/ui/search";
import { toast } from "react-toastify";
import { getItemsPerPage } from "../../../utils/CommonFunctions";
import CustomerService from "../../../../services/CustomerService";
import { colors } from "../../../config/colors";

import DeleteModal from "../../shared/DeleteModal";

const ListCustomer = () => {
  const navigate = useNavigate();
  const [pageVal, setPageVal] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(1);
  const [customerList, setCustomerList] = useState(null);
  const [searchVal, setSearchVal] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [showArchivedCustomers, setShowArchivedCustomers] = useState(false);
  const [archivingCustomer, setArchivingCustomer] = useState(null);
  const customersPerPage = getItemsPerPage();
  const SEARCH_DEBOUNCE_DELAY = 500;

  const handlePreviousPageClick = () => {
    if (pageVal === 1) return;
    setPageVal(pageVal - 1);
  };

  const handleNextPageClick = () => {
    if (pageVal >= totalPages) return;
    setPageVal(pageVal + 1);
  };

  const handleDeleteClick = (customerId) => {
    setCustomerToDelete(customerId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await CustomerService.deleteCustomer(customerToDelete);
      if (response.data.success) {
        toast.success("Customer deleted successfully");
        fetchCustomerList(); // Refresh the list
      } else {
        throw new Error("Failed to delete customer");
      }
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Failed to delete customer");
    } finally {
      setShowDeleteModal(false);
      setCustomerToDelete(null);
    }
  };

  const handleArchiveCustomer = async (customerId, isArchived = true) => {
    try {
      setArchivingCustomer(customerId);
      const response = await CustomerService.archiveCustomer(customerId, isArchived);
      if (response.data.success) {
        toast.success(isArchived ? "Customer archived successfully" : "Customer unarchived successfully");
        fetchCustomerList();
      } else {
        throw new Error(`Failed to ${isArchived ? 'archive' : 'unarchive'} customer`);
      }
    } catch (error) {
      console.error(`Error ${isArchived ? 'archiving' : 'unarchiving'} customer:`, error);
      toast.error(error.response?.data?.error || error.response?.data?.message || error.message || "Failed to archive customer");
    } finally {
      setArchivingCustomer(null);
    }
  };

  const fetchCustomerList = async (
    page = pageVal,
    limit = customersPerPage,
    search = searchVal
  ) => {
    setLoading(1);
    try {
      const response = await CustomerService.getCustomers(page, limit, search, showArchivedCustomers);

      if (response.data.success) {
        const { data, total, totalPages } = response.data;
        setCustomerList(data);
        setTotalCustomers(total);
        setTotalPages(totalPages);
        setLoading(0);
      } else {
        throw new Error("Failed to fetch customers");
      }
    } catch (error) {
      setLoading(2);
      console.error("Error fetching customer list:", error);
      toast.error("Failed to fetch customers");
    }
  };

  useEffect(() => {
    fetchCustomerList(pageVal, customersPerPage, searchVal);
  }, [pageVal, showArchivedCustomers]);

  useEffect(() => {
    if (pageVal === 1) {
      fetchCustomerList(1, customersPerPage, searchVal);
    } else {
      setPageVal(1);
    }
  }, [searchVal, showArchivedCustomers]);

  const handleEditClick = (customerId) => {
    navigate(`/edit-customer/${customerId}`);
  };

  const handleCreateCustomer = () => {
    navigate("/create-customer");
  };

  const handleViewClick = (customerId) => {
    navigate(`/view-customer/${customerId}`);
  };

  const pgItem1 =
    pageVal === 1
      ? 1
      : pageVal >= totalPages && totalPages > 2
        ? totalPages - 2
        : pageVal - 1;
  const pgItem2 =
    pageVal === 1
      ? 2
      : pageVal >= totalPages && totalPages > 2
        ? totalPages - 1
        : pageVal;
  const pgItem3 =
    pageVal === 1
      ? 3
      : pageVal >= totalPages && totalPages > 2
        ? totalPages
        : pageVal + 1;

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchVal(value);
    }, SEARCH_DEBOUNCE_DELAY),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    debouncedSearch(value);
  };

  function debounce(func, wait) {
    let timeout;
    const debouncedFunc = function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
    debouncedFunc.cancel = function () {
      clearTimeout(timeout);
    };
    return debouncedFunc;
  }

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 p-6">
      {/* Header Section with Stats */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h2 className="text-2xl font-bold text-primary-headings mb-2">
              {showArchivedCustomers ? 'Archived Customers' : 'Customer Management'}
            </h2>
            <p className="text-sm text-primary-light mb-6">
              {showArchivedCustomers ? 'View and manage archived customers' : 'Track and manage your customers efficiently'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowArchivedCustomers(!showArchivedCustomers)}
              className={`inline-flex items-center px-4 py-2 rounded-md border transition-colors duration-200 shadow-sm whitespace-nowrap ${showArchivedCustomers
                  ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
            >
              <IoMdArchive className="h-4 w-4 mr-2" />
              <span className="text-sm">{showArchivedCustomers ? 'Show Active Customers' : 'Show Archived Customers'}</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="bg-blue-50 p-3 rounded-lg">
                <svg className="w-6 h-6 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-primary-light">Total Customers</p>
                <p className="text-xl font-semibold text-primary-headings">{totalCustomers}</p>
              </div>
            </div>
          </div>

          {/* <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="bg-green-50 p-3 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-primary-light">Active Customers</p>
                <p className="text-xl font-semibold text-primary-headings">
                  {customerList?.length || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="bg-purple-50 p-3 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm text-primary-light">Engagement Rate</p>
                <p className="text-xl font-semibold text-primary-headings">
                  {Math.round((customerList?.length || 0) / (totalCustomers || 1) * 100)}%
                </p>
              </div>
            </div>
          </div> */}
        </div>

        {/* Search and Actions Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={handleSearchChange}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent text-sm"
              placeholder={showArchivedCustomers ? "Search archived customers..." : "Search customers..."}
            />
          </div>
          {!showArchivedCustomers && (
            <button
              onClick={handleCreateCustomer}
              className="inline-flex items-center px-6 py-2.5 text-white rounded-lg hover:bg-opacity-90 transition-all duration-200 shadow-sm font-medium whitespace-nowrap"
              style={{ backgroundColor: colors.highlights.callToAction }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Customer
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden max-h-[calc(100vh-480px)] flex flex-col mb-8">
        <div className="overflow-x-auto flex-1">
          <table className="w-full">
            <thead className="bg-white z-10 after:absolute after:inset-x-0 after:bottom-0 after:h-[1px] after:bg-gray-200">
              <tr>
                <th className="w-[60px] min-w-[60px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">No</th>
                <th className="w-full min-w-[200px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Name</th>
                <th className="w-[200px] min-w-[200px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Email</th>
                <th className="w-[150px] min-w-[150px] px-4 py-3 text-left text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Phone</th>
                <th className="w-[120px] min-w-[120px] px-4 py-3 text-center text-xs font-semibold text-primary-headings uppercase tracking-wider sticky top-0 bg-gray-100">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading === 1 && (
                <tr>
                  <td colSpan="5" className="px-4 py-8">
                    <div className="flex items-center justify-center">
                      <div className="w-8 h-8 border-4 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: colors.primary.headingsBody }}></div>
                    </div>
                  </td>
                </tr>
              )}

              {loading === 0 && customerList?.map((customer, index) => (
                <tr
                  key={customer._id}
                  className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                  onClick={() => handleViewClick(customer._id)}
                >
                  <td className="w-[60px] min-w-[60px] px-4 py-3 text-sm font-medium text-primary-headings whitespace-nowrap">
                    {(pageVal - 1) * customersPerPage + index + 1}
                  </td>
                  <td className="w-full min-w-[200px] px-4 py-3">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-primary-headings truncate">
                        {customer.name}
                        {showArchivedCustomers && (
                          <span className="text-xs text-yellow-600 ml-2 bg-yellow-50 px-2 py-1 rounded-full italic">Archived</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="w-[200px] min-w-[200px] px-4 py-3">
                    <span className="text-sm text-primary-light truncate block">
                      {customer.email}
                    </span>
                  </td>
                  <td className="w-[150px] min-w-[150px] px-4 py-3">
                    <span className="text-sm text-primary-light whitespace-nowrap">
                      {customer.phoneNumber}
                    </span>
                  </td>
                  <td className="w-[120px] min-w-[120px] px-4 py-3">
                    <div className="flex justify-center space-x-2 overflow-x-auto">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewClick(customer._id);
                        }}
                        className="p-1.5 hover:bg-blue-50 rounded-lg transition-colors duration-200 shrink-0"
                      >
                        <IoEyeOutline size={18} className="text-blue-500" />
                      </button>
                      {!showArchivedCustomers && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(customer._id);
                          }}
                          className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors duration-200 shrink-0"
                        >
                          <HiOutlinePencilAlt size={18} className="text-indigo-500" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchiveCustomer(customer._id, !showArchivedCustomers);
                        }}
                        className="p-1.5 hover:bg-yellow-50 rounded-lg transition-colors duration-200 shrink-0"
                        title={showArchivedCustomers ? "Unarchive" : "Archive"}
                        disabled={archivingCustomer === customer._id}
                      >
                        {archivingCustomer === customer._id ? (
                          <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <IoMdArchive size={18} className="text-yellow-600" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {loading === 0 && (!customerList || customerList.length === 0) && (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <IoSearch size={48} className="text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
                      <p className="text-gray-500">Try adjusting your search or filters to find what you're looking for.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {loading === 2 && (
          <div className="flex flex-col items-center py-8">
            <MdOutlineWifiTetheringErrorRounded size={120} className="text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-800 mt-4">Error fetching data</h2>
            <h3 className="text-gray-500 mt-2">Check your network and try again</h3>
          </div>
        )}

        {/* Pagination Section */}
        {loading === 0 && customerList?.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-100 bg-white">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm text-primary-light">
                Showing <span className="font-medium">{customersPerPage * (pageVal - 1) + 1}</span> to{' '}
                <span className="font-medium">{customersPerPage * (pageVal - 1) + customerList.length}</span> of{' '}
                <span className="font-medium">{totalCustomers}</span> results
              </p>

              <nav className="flex items-center gap-2">
                <button
                  onClick={handlePreviousPageClick}
                  disabled={pageVal === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50
                    ${pageVal === 1 ? 'bg-gray-100 text-primary-light' : 'bg-primary-headings text-white'}`}
                >
                  Previous
                </button>

                {totalPages > 0 && (
                  <>
                    <button
                      className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                        ${pageVal === pgItem1 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                      onClick={() => setPageVal(pgItem1)}
                    >
                      {pgItem1}
                    </button>
                    {totalPages > 1 && (
                      <button
                        className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                          ${pageVal === pgItem2 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                        onClick={() => setPageVal(pgItem2)}
                      >
                        {pgItem2}
                      </button>
                    )}
                    {totalPages > 2 && (
                      <button
                        className={`px-3 py-1 rounded-md text-sm font-medium hover:bg-gray-200
                          ${pageVal === pgItem3 ? 'bg-primary-headings text-white' : 'bg-gray-100 text-primary-light'}`}
                        onClick={() => setPageVal(pgItem3)}
                      >
                        {pgItem3}
                      </button>
                    )}
                  </>
                )}

                <button
                  onClick={handleNextPageClick}
                  disabled={pageVal >= totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 disabled:opacity-50
                    ${pageVal >= totalPages ? 'bg-gray-100 text-primary-light' : 'bg-primary-headings text-white'}`}
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <DeleteModal
          deleteItem="Customer"
          onDelete={handleConfirmDelete}
          onClose={() => {
            setShowDeleteModal(false);
            setCustomerToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default ListCustomer;
